// Interfaces TypeScript para o teste de nivelamento
// Geradas automaticamente dos DTOs C# (simulação)

export enum LevelTestStates {
  Inactive = 0,   // não há sessão ativa
  Active = 1,     // sessão ativa sem respostas
  InProgress = 2, // sessão ativa com respostas
  Completed = 3,  // sessão finalizada
  Skipped = 4,    // estudante pulou o teste (início pelos primeiros módulos)
}

export interface LevelTestQuestion {
  id: string;
  questionText: string;
  questionType: 'multiple_choice';
  options: LevelTestOption[];
  difficultyLevel: number;
  skillArea: 'grammar' | 'vocabulary' | 'comprehension' | 'general';
}

export interface LevelTestOption {
  id: string;
  text: string;
  order: number;
}

export interface LevelTestSession {
  id: string;
  userId: string;
  status: 'Started' | 'InProgress' | 'Completed';
  currentQuestionIndex: number;
  startedAt: string;
  completedAt?: string;
  questions: LevelTestQuestion[];
  answers: LevelTestAnswer[];
}

export interface LevelTestAnswer {
  questionId: string;
  selectedOptionIds: string[];
  isCorrect: boolean;
  answeredAt: string;
  timeSpentMs: number;
}

export interface LevelTestResult {
  id: string;
  sessionId?: string;  // Opcional: pode vir do backend como SessionId ou ser igual a id
  userId?: string;  // Opcional: não retornado pelo backend
  finalLevel: string | number;  // Pode ser string ou número
  finalLevelName?: string; // Nome do nível retornado pelo backend (ex: "Beginner", "Intermediate")
  scorePercentage: number;
  correctAnswers: number;
  skillBreakdown: SkillBreakdown;
  completedAt?: string;  // Opcional: pode vir como CompletedAt
}

export interface SkillBreakdown {
  skills: Record<string, number>; // Estrutura dinâmica: chave é o nome da skill area, valor é a porcentagem
  overall: number; // Porcentagem geral
}

// Request/Response DTOs
export interface StartLevelTestRequest {
  userId: string;
}

export interface StartLevelTestResponse {
  sessionId: string;
  firstQuestion: LevelTestQuestion;
}

export interface SubmitAnswerRequest {
  sessionId: string;
  questionId: string;
  selectedOptionIds: string[];
  timeSpentMs: number;
}

export interface SubmitAnswerResponse {
  answer: {
    exerciseId: string;
    answerData: string;
    isCorrect: boolean;
    answeredAt: string;
  };
  isTestComplete: boolean;
}

export interface GetLevelTestStatusRequest {
  userId: string;
}

export interface GetLevelTestStatusResponse {
  hasCompletedTest: boolean;
  currentSession?: LevelTestSession;
  lastResult?: LevelTestResult;
  lastCompletedSessionId?: string;  // ID da última sessão completada (do backend)
}

export interface GetLevelTestResultRequest {
  sessionId: string;
}

export type GetLevelTestResultResponse = LevelTestResult;

// Error Response
export interface LevelTestError {
  message: string;
  code: string;
  details?: Record<string, any>;
}

// Local storage data structure
export interface LevelTestLocalData {
  sessionId?: string;
  currentQuestionIndex: number;
  answers: LevelTestAnswer[];
  startedAt: string;
  lastUpdated: string;
  version: number;
}

// React Query keys
export const LEVEL_TEST_QUERY_KEYS = {
  status: (userId: string) => ['level-test', 'status', userId],
  session: (sessionId: string) => ['level-test', 'session', sessionId],
  result: (sessionId: string) => ['level-test', 'result', sessionId],
} as const;

// Snapshot & Next Question DTOs
export interface LevelTestAnsweredItem {
  order: number;
  question: LevelTestQuestion;
  answer: LevelTestAnswer;
}

export interface GetSessionSnapshotResponse {
  sessionId: string;
  answered: LevelTestAnsweredItem[];
  isTestCompleted: boolean;
}

export interface GetNextQuestionResponse {
  sessionId: string;
  questionNumber?: number;
  question?: LevelTestQuestion;
  isTestCompleted: boolean;
}
